import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LatestJoblogPackage } from '../interfaces/latest-joblog-package.interface';
import { PagedResult } from '../interfaces/paged-result.interface';
import { MatchedArticle } from '../interfaces/matched-article.interface';
import { JobLogMatchInput } from '../interfaces/joblog-match-input.interface';

export interface LogRow {
  id: number;
  creationDate: string;
  message: string;

  fromApplicationId?: number | null;
  toApplicationId?: number | null;

  linkTypeId?: number | null;
}

export interface PatternSearchRequest {
  terms: string[];
  chunks: string[];
  take: number;
  skip: number;
  linkId?: number | null;
  jobLogType?: number | null;
  fromApplicationId: number | null;
  toApplicationId: number | null;
}

export interface JobLogBatchInspector {
	lookupId: string;
	resolvedFrom: string;
	sessionId: string;
	linkId: number;
	companyId?: number | null;
	jobCreationDate?: string | null;
	jobCompletionDate?: string | null;
	totalBatches: number;
	runningBatches: number;
	completedBatches: number;
	failedBatches: number;
	pendingBatches: number;
	totalItems: number;
	failedItems: number;
	batches: JobLogBatchInspectorBatch[];
}

export interface JobLogBatchInspectorBatch {
	queueBatchId: number;
	queueId: number;
	batchIndex: number;
	durableInstanceId?: string | null;
	status: number;
	statusText: string;
	startDate?: string | null;
	endDate?: string | null;
	creationDate: string;
	logId?: string | null;
	traceParent?: string | null;
	totalItems: number;
	unprocessedItems: number;
	processedItems: number;
	completedItems: number;
	failedItems: number;
	items: JobLogBatchInspectorItem[];
}

export interface JobLogBatchInspectorItem {
	id: number;
	queueBatchId: number;
	objectId: string;
	status: number;
	statusText: string;
	creationDate: string;
}

@Injectable({
  providedIn: 'root',
})
export class JobLogService {
  readonly url: string = 'JobLog';
  constructor(private http: HttpClient) { }

  public get(instanceId: string, companyId?: number): Observable<any> {
    return this.http.get<any>(this.url + '/getJobFields/' + instanceId, {
      headers: this.getCompanyHeaders(companyId),
    });
  }

	public getBatchInspector(instanceIdOrDurableInstanceId: string, linkId: number, companyId?: number): Observable<JobLogBatchInspector> {
		return this.http.get<JobLogBatchInspector>(`${this.url}/getBatchInspector/${encodeURIComponent(instanceIdOrDurableInstanceId)}`, {
			headers: this.getCompanyHeaders(companyId),
			params: {
				linkId: linkId.toString(),
			},
		});
	}

  public getMatchedArticles(postBody: {correlationId: string, matchInput: JobLogMatchInput}): Observable<MatchedArticle[]> {
    return this.http.post<MatchedArticle[]>(this.url + '/getMatchedArticles/', postBody);
  }

  public logFrontendException(postBody: {linkId: number, message: string, stackTrace?: string}): Observable<string> {
    return this.http.post<string>(this.url + '/logFrontendException/', postBody);
  }

  public getLatestFieldsByLogType(latestJoblogPackage: LatestJoblogPackage, companyId?: number): Observable<any> {
    return this.http.post<any>(this.url + '/getNewJobFieldsById', latestJoblogPackage, {
      headers: this.getCompanyHeaders(companyId),
    });
  }

  private getCompanyHeaders(companyId?: number): HttpHeaders | undefined {
    return companyId != null ? new HttpHeaders().set('X-company-id', companyId.toString()) : undefined;
  }

  searchByUserPattern(req: {
    terms: string[];
    chunks: string[];
    take: number;
    skip: number;
    linkId: number | null;
    jobLogType: number | null;
    fromApplicationId: number | null;
    toApplicationId: number | null;
    linkTypeId: number | null;
    includeTotal: boolean;
    uniqueOnly: boolean;
    searchInStackTrace: boolean;
  }): Observable<PagedResult<LogRow>> {
    return this.http.post<PagedResult<LogRow>>('/api/JobLog/search-by-user-pattern', req);
  }
}
